import * as S from './styles';
import { colors } from '@/constants';
import { ClientProps, DiscountProps } from '@/types';
import React, { useMemo } from 'react';
import { formatCode, formatCurrency, getOnStorage } from '@/shared';
import { useGlobals, useTables, useUserContext } from '@/hooks';
import { Accordion, ToolTip, ShortProgressBar, SeloCampanha } from '@/components';
import { calcularDiasAtras, formatarCEP, formatarData, formatarTelefone } from '@/shared/commonFunctions';
import {
  RiMapPin2Line,
  RiExchangeDollarLine,
  RiCalendarEventLine,
  RiDirectionLine,
  RiCalendarScheduleLine,
  RiFlag2Line,
} from 'react-icons/ri';

const types = {
  1: 'default',
  2: 'success',
  3: 'warning',
  4: 'danger',
};

const { userAgent } = navigator;
const tabletRegex = /iPad|tablet|playbook|silk|Macintosh|Windows|Linux|X11/i;
const mobileRegex = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i;
const isMobile = mobileRegex.test(userAgent) && !tabletRegex.test(userAgent);

const screenWidth = window.innerWidth;
const maxWidth = isMobile || screenWidth < 728 ? `${screenWidth - 30}px` : '80%';

export interface Props {
  index: number;
  isChecked?: boolean;
  showProgress?: boolean;
  client: ClientProps;
  setClient: (value: ClientProps | undefined) => void;
  isExpanded: boolean;
  toggleExpansion: () => void;

  // ⬇️ Novos callbacks (modal controlado pelo pai)
  onOpenBoleto: (client: ClientProps) => void;
  onOpenCreditLimit: (client: ClientProps) => void;
}

const ClientCard = ({
  showProgress,
  isChecked,
  client,
  setClient,
  isExpanded,
  toggleExpansion,
  onOpenBoleto,
  onOpenCreditLimit,
}: Props) => {
  const { tables } = useTables();
  const { customer } = useUserContext();
  const { setCurrentModal } = useGlobals();

  const discounts = getOnStorage('discounts') as {
    [key: number]: DiscountProps;
  };

  const dateList = useMemo(
    () =>
      JSON.parse(JSON.stringify(client.DT_ULTIMA_COMPRA)).sort((a: any, b: any) =>
        a.dta_ult_compra.replace('/', '') < b.dta_ult_compra.replace('/', '') ? 1 : -1
      ) || [],
    [client.DT_ULTIMA_COMPRA, customer?.codPessoa]
  );
  const hasDiscount = useMemo(() => !!discounts?.[client.COD_CLIENTE], [client.COD_CLIENTE, discounts]);

  const { since, until, currentStatus, currentWeek } = useMemo(() => {
    const since = formatarData(Math.min(...(client?.CYCLE?.map((c) => c.DE) || [])) || '', { year: '2-digit' });
    const until = formatarData(Math.max(...(client?.CYCLE?.map((c) => c.ATE) || [])) || '', { year: '2-digit' });
    const current = client.CYCLE.find((cy) => cy.SEMANA_CICLO_ID_VIGENTE === cy.SEMANA_CICLO_ID);
    const currentWeek = current?.SEMANA_CICLO_ID_VIGENTE || 0;
    const currentStatus = current?.STATUS_POSITIVACAO || 1;
    return { since, until, currentStatus, currentWeek };
  }, [client.CYCLE]);

  const dtUltimaCompra = useMemo(() => {
    if (!dateList.length) return 0;

    const date = [...dateList].reduce((acc: any, dt: any) => {
      const isMine = dt.nro_representante === customer?.codPessoa;
      const isMoreRecent = acc.replace('/', '') < dt.dta_ult_compra.replace('/', '');

      if (isMoreRecent && isMine) return dt.dta_ult_compra;
      return acc;
    }, dateList[0]?.dta_ult_compra);

    return date || dateList[0]?.dta_ult_compra;
  }, [client, customer?.codPessoa, dateList]);

  const datesMsg = useMemo(() => {
    if (!dateList?.length) return '';
    const List = dateList.map((date: any) => (
      <S.Row
        key={`${date.nro_segmento}-${date.dta_ult_compra}-${date.nro_representante}`}
        $marginTop={3}
        $marginBottom={3}
        $jcenter
      >
        <S.LabelLastPurchase key={date.nro_segmento}>
          <S.IndicatorMySales $isMine={date.nro_representante === customer?.codPessoa} />
          Segmento:&nbsp;
          <span>{date.nro_segmento}</span>&nbsp;&nbsp;&nbsp; Data da última Compra:&nbsp;
          <span>{formatarData(date.dta_ult_compra)}</span>
        </S.LabelLastPurchase>
      </S.Row>
    ));
    return <S.Column key={dtUltimaCompra}>{List}</S.Column>;
  }, [dtUltimaCompra, dateList, customer?.codPessoa]);

  const isDisabled = client.ST_COMPRA === 'I';
  const isDanger = client.daysWithoutPurchase >= 60;
  const isWarning = client.daysWithoutPurchase >= 30 && !isDanger;
  const hasAlert = isDanger || isWarning;
  const currentType = types[currentStatus] as S.CardColor;
  const pedidos = tables.current.eventos?.[client.COD_CLIENTE]?.boletos;

  const handleOpenBoletoModal = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (pedidos?.length) {
      setCurrentModal(`modal-boleto-${client.COD_CLIENTE}`);
      onOpenBoleto(client);
    }
  };

  const handleOpenCreditLimitModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentModal(`modal-credit-limit-${client.COD_CLIENTE}`);
    onOpenCreditLimit(client);
  };

  // Aqui está o segredo: só esse onClick faz a seleção!
  return (
    <S.CheckedBox
      $ischecked={isChecked}
      onClick={() => !isDisabled && setClient(isChecked ? undefined : client)}
      style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', width: '-webkit-fill-available' }}
    >
      <S.Card $themecolor={currentType} style={{ width: '100%' }}>
        <S.ContentInfos $space={!isExpanded}>
          <S.InfosContent>
            <S.Row $between>
              <S.Column>
                <SeloCampanha mode="cliente" codCliente={client.COD_CLIENTE} />
              </S.Column>
              <S.Column>
                <S.CompanieName data-test={`card-company-title-${client.COD_CLIENTE}`}>
                  <S.NumberCompanie $themecolor={currentType} $isActive={isChecked}>
                    {client.COD_CLIENTE}
                  </S.NumberCompanie>
                  <S.FantasyName $isdisabled={isDisabled}>{client.NM_CLIENTE}</S.FantasyName>
                </S.CompanieName>
                <S.CNPJLabel $isdisabled={isDisabled}>{formatCode(client.NR_DOCUMENTO)}</S.CNPJLabel>
              </S.Column>
              <S.ColumnRight>
                <S.Row>
                  {hasDiscount && <RiExchangeDollarLine color={colors.alert.green300} fontSize={24} />}
                  {isDisabled ? (
                    <S.ContentProhibited>
                      <S.ProhibitedIcon size={16} />
                    </S.ContentProhibited>
                  ) : (
                    <S.CheckContent>
                      <S.CheboxContent $isdisabled={isDisabled}>
                        {isChecked ? <S.CheckboxCircle size={24} /> : <S.CheckboxBlankCircle size={24} />}
                      </S.CheboxContent>
                    </S.CheckContent>
                  )}
                </S.Row>
                <S.Row $marginTop={4}>
                  {!client.notifications && (
                    <S.ContentBar className={!pedidos?.length ? 'disabled' : ''} onClick={handleOpenBoletoModal}>
                      <S.BarIcon size={24} />
                      {pedidos?.length && <S.BarNotification>{pedidos?.length}</S.BarNotification>}
                    </S.ContentBar>
                  )}
                  <ToolTip text="Em breve" displayMode="hover">
                    <RiDirectionLine className="disabled" fontSize={24} color={colors.gray.gray50} />
                  </ToolTip>
                </S.Row>
              </S.ColumnRight>
            </S.Row>

            {!!dtUltimaCompra && (
              <ToolTip maxWidth={maxWidth} text={datesMsg} displayMode="hover">
                <S.LastPurchaseContent $isdisabled={isDisabled} $themecolor={currentType}>
                  {currentType === 'warning' && <RiFlag2Line color={colors.gray.gray50} fontSize={18} />}
                  {currentType === 'default' && <RiCalendarEventLine color={colors.gray.gray50} fontSize={18} />}
                  {currentType === 'success' && <RiCalendarEventLine color={colors.gray.gray50} fontSize={18} />}
                  {currentType === 'danger' && <RiCalendarScheduleLine color={colors.gray.gray50} fontSize={18} />}
                  <S.LastPurchase>
                    Última compra em {formatarData(dtUltimaCompra)} - {calcularDiasAtras(dtUltimaCompra)} dias
                  </S.LastPurchase>
                </S.LastPurchaseContent>
              </ToolTip>
            )}

            <S.AddresContent $isdisabled={isDisabled}>
              <S.MapContent $themecolor={currentType}>
                <RiMapPin2Line fontSize={18} />
              </S.MapContent>
              <S.Address>
                {`${client.ENDERECO}`}, {`${client.NUMERO}`} - {`${client.BAIRRO}`}, {`${client.CIDADE}`} - {`${client.ESTADO}`}
                <S.PRow $between>
                  <S.CEP>{formatarCEP(`${client.CEP}`)}</S.CEP>
                  <S.Phone>{formatarTelefone(client.NR_TELEFONE)}</S.Phone>
                </S.PRow>
              </S.Address>
            </S.AddresContent>

            <S.CycleContent $isdisabled={isDisabled}>
              <S.Cycle>
                <S.Label $marginBottom={12} $weight={500}>
                  Ciclo
                </S.Label>
                <S.SubLabel>
                  {since} a {until}
                </S.SubLabel>
                {!showProgress && (
                  <S.Row $marginTop={4} $between>
                    <S.ProgressContent>
                      {client.CYCLE.map((cycle) => {
                        return (
                          <ShortProgressBar
                            key={cycle.SEMANA_CICLO_ID}
                            isCurrent={cycle.SEMANA_CICLO_ID_VIGENTE === currentWeek}
                            state={cycle.POSITIVOU ? 'checked' : 'empty'}
                          />
                        );
                      })}
                    </S.ProgressContent>
                  </S.Row>
                )}
              </S.Cycle>
              <S.Separator />
              <S.Positivacao>
                <ToolTip text="Em breve" displayMode="hover">
                  <S.CardTitle className="disabled">Positivação de clientes</S.CardTitle>
                  <S.Row className="disabled" $between>
                    <S.SubLabel>Atingido</S.SubLabel>
                    <S.SubLabel>Pendente</S.SubLabel>
                  </S.Row>
                  <S.Row className="disabled" $between>
                    <S.Label>R$ ----</S.Label>
                    <S.Label>R$ ----</S.Label>
                  </S.Row>
                </ToolTip>
              </S.Positivacao>
            </S.CycleContent>
          </S.InfosContent>
        </S.ContentInfos>

        <S.ContentAlert $themecolor={currentType} $showBorder={hasAlert || isExpanded}>
          {hasAlert && (
            <S.AlertMessage type={isDanger ? 'danger' : 'warning'}>
              {isWarning && <S.FlagIcon size={15} />}
              {isDanger && <S.CalendarIcon size={15} />}
              Não compra há mais de
              <S.AlertMessageBold>{client.daysWithoutPurchase} dias</S.AlertMessageBold>
            </S.AlertMessage>
          )}
          <Accordion
            noRadius
            noBodyPadding
            accordionTitle=""
            showHeader={false}
            bgColor={colors.transparent}
            accordionExpanded={isExpanded}
            setAccordionExpanded={toggleExpansion}
            id={`company-${client.COD_CLIENTE}`}
          >
            <S.AccordionContent>
              <S.AccordionInfos $themecolor={currentType}>
                <S.AccordionDataInfo>
                  <S.AccordionLabel>Limite de crédito:</S.AccordionLabel>
                  <S.AccordionLimit>{formatCurrency({ value: client.LIMITE_CREDITO })}</S.AccordionLimit>
                </S.AccordionDataInfo>
                <S.AccordionDataInfo>
                  <S.AccordionLabel>Saldo disponível:</S.AccordionLabel>
                  <S.AccordionLimit>{formatCurrency({ value: client?.SALDO_DISPONIVEL || 0 })}</S.AccordionLimit>
                </S.AccordionDataInfo>
              </S.AccordionInfos>
              <S.RequestLimit onClick={handleOpenCreditLimitModal}>
                <S.IconPlus size={18} />
                Solicitar mais limite de crédito e/ou desbloqueio de cliente
              </S.RequestLimit>
            </S.AccordionContent>
          </Accordion>

          <S.ArrowContent
            $isOpened={isExpanded}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpansion();
            }}
            style={{ cursor: 'pointer' }}
          >
            <S.OpenDetails>{!isExpanded ? 'Mais' : 'Menos'} detalhes</S.OpenDetails>
            <S.ArrowIcon size={15} $isOpened={isExpanded} />
          </S.ArrowContent>
        </S.ContentAlert>
      </S.Card>
    </S.CheckedBox>
  );
};

export default React.memo(ClientCard);
